import { AnimationHistoryEntry } from "../../../backend/useAnimate";
import { defaultConfig } from "../../../utils/config";

const VIDEO_TRANSFORM_APPROACH = defaultConfig.VIDEO_TRANSFORM_APPROACH;
const CLOUDFLARE_FFMPEG_URL = process.env.REACT_APP_CLOUDFLARE_FFMPEG_URL || '';

export interface FFmpegRequest {
    action: 'split_frames' | 'export';
    videoUrl?: string;
    animationId: string;
    images?: string[];
    format?: string; // 'mp4', 'webm', 'gif', 'sprite_sheet'
    fps?: number;
    rows?: number;
    cols?: number;
    sheetWidth?: number;
    sheetHeight?: number;
    discardFirstFrame?: boolean;
}

export interface FFmpegSplitFramesResult {
    historyEntry: AnimationHistoryEntry;
}

export interface FFmpegExportResult {
    url: string;
}

/**
 * Helper to route requests based on the VIDEO_TRANSFORM_APPROACH
 */
const invokeFFmpeg = async (body: FFmpegRequest): Promise<any> => {
    // console.log(`FFmpeg Service: Using approach "${VIDEO_TRANSFORM_APPROACH}"`);

    if (VIDEO_TRANSFORM_APPROACH === 'cloudflare') {
        if (!CLOUDFLARE_FFMPEG_URL) {
            throw new Error("CLOUDFLARE_FFMPEG_URL is not defined in environment variables.");
        }

        const response = await fetch(CLOUDFLARE_FFMPEG_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Cloudflare Error: ${errorText || response.statusText}`);
        }

        return await response.json();
    } else {
        throw new Error(`Invalid VIDEO_TRANSFORM_APPROACH: ${VIDEO_TRANSFORM_APPROACH}`);
    }
};

export const ffmpegService = {
    /**
     * Calls the backend to split video frames.
     */
    splitFrames: async (params: {
        videoUrl: string;
        animationId: string;
        fps?: number;
        discardFirstFrame?: boolean;
    }): Promise<FFmpegSplitFramesResult> => {
        const body: FFmpegRequest = {
            action: 'split_frames',
            ...params
        };

        const data = await invokeFFmpeg(body);
        return data as FFmpegSplitFramesResult;
    },

    /**
     * Calls the backend to export frames to a video or gif.
     */
    exportAnimation: async (params: {
        images: string[];
        format: string; // 'mp4', 'webm', 'gif', 'sprite_sheet'
        animationId: string;
        fps?: number;
        rows?: number;
        cols?: number;
        sheetWidth?: number;
        sheetHeight?: number;
    }): Promise<FFmpegExportResult> => {
        const body: FFmpegRequest = {
            action: 'export',
            ...params
        };

        const data = await invokeFFmpeg(body);
        return data as FFmpegExportResult;
    }
};